import {
  clearActionResponse,
  updateActionData,
} from "actions/pluginActionActions";
import type {
  EventType,
  ExecuteTriggerPayload,
  TriggerSource,
} from "constants/AppsmithActionConstants/ActionConstants";
import { TriggerKind } from "constants/AppsmithActionConstants/ActionConstants";
import type { ReduxAction } from "ee/constants/ReduxActionConstants";
import { ReduxActionTypes } from "ee/constants/ReduxActionConstants";
import type { AppState } from "ee/reducers";
import type { ActionDescription } from "ee/workers/Evaluation/fns";
import * as log from "loglevel";
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import {
  initiateApprovalProcessSaga,
  submitApprovalProcessFormSaga,
} from "sagas/ActionExecution/ApprovalProcessSaga";
import copySaga from "sagas/ActionExecution/CopyActionSaga";
import downloadSaga from "sagas/ActionExecution/DownloadActionSaga";
import {
  deleteDraftActionSaga,
  restoreDraftActionSaga,
  saveDraftActionSaga,
} from "sagas/ActionExecution/DraftSage";
import {
  getCurrentLocationSaga,
  getEchartSaga,
  stopWatchCurrentLocation,
  watchCurrentLocation,
} from "sagas/ActionExecution/geolocationSaga";
import { logoutActionSaga } from "sagas/ActionExecution/LogoutActionSaga";
import {
  closeModalSaga,
  openModalSaga,
} from "sagas/ActionExecution/ModalSagas";
import {
  closeDrawerSaga,
  openDrawerSaga,
} from "sagas/ActionExecution/DrawerSagas";
import navigateActionSaga from "sagas/ActionExecution/NavigateActionSaga";
import executePluginActionTriggerSaga from "sagas/ActionExecution/PluginActionSaga";
import { postMessageSaga } from "sagas/ActionExecution/PostMessageSaga";
import printSaga from "sagas/ActionExecution/PrintActionSaga";
import resetWidgetActionSaga from "sagas/ActionExecution/ResetWidgetActionSaga";
import showAlertSaga from "sagas/ActionExecution/ShowAlertActionSaga";
import {
  unlistenWindowMessageSaga,
  windowMessageListenerSaga,
} from "sagas/ActionExecution/WindowMessageSaga";
import {
  evaluateActionSelectorFieldSaga,
  evaluateAndExecuteDynamicTrigger,
  setAppVersionOnWorkerSaga,
} from "sagas/EvaluationsSaga";
import { getActionById } from "selectors/editorSelectors";
import AppsmithConsole from "utils/AppsmithConsole";

export interface TriggerMeta {
  source?: TriggerSource;
  triggerPropertyName?: string;
  triggerKind?: TriggerKind;
}

/**
 * The controller saga that routes different trigger effects to its executor sagas
 * @param trigger The trigger information with trigger type
 * @param eventType Widget/Platform event which triggered this action
 * @param triggerMeta Where the trigger originated from
 */
export function* executeActionTriggers(
  trigger: ActionDescription,
  eventType: EventType,
  triggerMeta: TriggerMeta,
): any {
  // when called via a promise, a trigger can return some value to be used in .then
  let response: unknown[] = [];
  switch (trigger.type) {
    case "PRINT":
      yield call(printSaga, trigger);
      break;
    case "SAVE_DRAFT":
      yield call(saveDraftActionSaga, trigger);
      break;
    case "RESTORE_DRAFT":
      yield call(restoreDraftActionSaga, trigger);
      break;
    case "DELETE_DRAFT":
      yield call(deleteDraftActionSaga, trigger);
      break;
    case "RUN_PLUGIN_ACTION":
      response = yield call(executePluginActionTriggerSaga, trigger, eventType);
      break;
    case "CLEAR_PLUGIN_ACTION":
      yield put(clearActionResponse(trigger.payload.actionId));
      const action: ReturnType<typeof getActionById> = yield select(
        (state: AppState) =>
          getActionById(state, {
            match: {
              params: {
                apiId: trigger.payload.actionId,
              },
            },
          }),
      );
      if (action) {
        yield put(
          updateActionData([
            {
              entityName: action.name,
              dataPath: "data",
              data: undefined,
            },
          ]),
        );
      }
      break;
    case "NAVIGATE_TO":
      yield call(navigateActionSaga, trigger);
      break;
    case "SHOW_ALERT":
      yield call(showAlertSaga, trigger);
      break;
    case "LOGOUT":
      yield call(logoutActionSaga, trigger);
      break;
    case "SHOW_MODAL_BY_NAME":
      yield call(openModalSaga, trigger);
      break;
    case "CLOSE_MODAL":
      yield call(closeModalSaga, trigger);
      break;
    case "OPEN_DRAWER_BY_NAME":
      yield call(openDrawerSaga, trigger);
      break;
    case "CLOSE_DRAWER":
      yield call(closeDrawerSaga, trigger);
      break;
    case "DOWNLOAD":
      yield call(downloadSaga, trigger);
      break;
    case "COPY_TO_CLIPBOARD":
      yield call(copySaga, trigger);
      break;
    case "RESET_WIDGET_META_RECURSIVE_BY_NAME":
      yield call(resetWidgetActionSaga, trigger);
      break;
    case "GET_CURRENT_LOCATION":
      response = yield call(getCurrentLocationSaga, trigger);
      break;
    case "CALL_FUNC":
      response = yield call(
        getEchartSaga,
        trigger.payload,
        eventType,
        triggerMeta,
      );
      break;
    case "WATCH_CURRENT_LOCATION":
      response = yield call(
        watchCurrentLocation,
        trigger,
        eventType,
        triggerMeta,
      );
      break;
    case "STOP_WATCHING_CURRENT_LOCATION":
      response = yield call(stopWatchCurrentLocation);
      break;
    case "POST_MESSAGE":
      yield call(postMessageSaga, trigger);
      break;
    case "WINDOW_MESSAGE_LISTENER":
      log.info(
        "\n[[ MESSAGE ]]\t[ saga ] (0) 执行 WINDOW_MESSAGE_LISTENER trigger saga",
      );
      response = yield call(
        windowMessageListenerSaga,
        trigger,
        eventType,
        triggerMeta,
      );
      break;
    case "UNLISTEN_WINDOW_MESSAGE":
      log.info(
        "\n[[ MESSAGE ]]\t[ saga ] (0) 执行 UNLISTEN_WINDOW_MESSAGE trigger saga",
      );
      response = yield call(
        unlistenWindowMessageSaga,
        trigger,
        eventType,
        triggerMeta,
      );
      break;
    case "INITIATE_APPROVAL_PROCESS":
      yield call(initiateApprovalProcessSaga, trigger);
      break;
    case "SUBMIT_APPROVAL_PROCESS_FORM":
      yield call(submitApprovalProcessFormSaga, trigger);
      break;
    default:
      log.error("Trigger type unknown", trigger);
      throw Error("Trigger type unknown");
  }
  return response;
}

export function* executeAppAction(payload: ExecuteTriggerPayload): any {
  const {
    callbackData,
    dynamicString,
    event: { type },
    globalContext,
    source,
    triggerPropertyName,
  } = payload;

  log.debug({ dynamicString, callbackData, globalContext });
  if (dynamicString === undefined) {
    throw new Error("Executing undefined action");
  }

  return yield call(
    evaluateAndExecuteDynamicTrigger,
    dynamicString,
    type,
    {
      source,
      triggerPropertyName,
      triggerKind: TriggerKind.EVENT_EXECUTION,
    },
    callbackData,
    globalContext,
  );
}

function* initiateActionTriggerExecution(
  action: ReduxAction<ExecuteTriggerPayload>,
): Generator<any, void, any> {
  const { event, source, triggerPropertyName } = action.payload;
  // Clear all error for this action trigger. In case the error still exists,
  // it will be created again while execution
  AppsmithConsole.deleteErrors([
    { id: `${source?.id}-${triggerPropertyName}` },
  ]);
  try {
    const result = yield call(executeAppAction, action.payload);
    if (event.callback) {
      event.callback({ success: true, response: result });
    }
  } catch (e) {
    if (event.callback) {
      event.callback({ success: false });
    }
    log.error(e);
  }
}

export function* watchActionExecutionSagas() {
  yield all([
    takeEvery(
      ReduxActionTypes.EXECUTE_TRIGGER_REQUEST,
      initiateActionTriggerExecution,
    ),
    takeLatest(
      ReduxActionTypes.SET_APP_VERSION_ON_WORKER,
      setAppVersionOnWorkerSaga,
    ),
    takeLatest(
      ReduxActionTypes.EVALUATE_ACTION_SELECTOR_FIELD,
      evaluateActionSelectorFieldSaga,
    ),
  ]);
}
